import cn from "mcn";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { LuCheck } from "react-icons/lu";
import type { Column } from "ag-grid-community";

import { ActionsIcon } from "../../icons";
import { ChevronRightIcon } from "../../icons/ChevronRightIcon";
import { ChangeSourceIcon } from "../../icons/ChangeSourceIcon";
import { FuturesFieldIcon } from "../../icons/FuturesFieldIcon";
import { ReconfirmDataIcon } from "../../icons/ReconfirmDataIcon";
import { PauseExchangeIcon } from "../../icons/PauseExchangeIcon";
import { ColumnWidthsIcon } from "../../icons/ColumnWidthsIcon";
import { ClearFormattingIcon } from "../../icons/ClearFormattingIcon";
import { MenuItemIconWrapper } from "../../icons/MenuItemIconWrapper";
import { Tooltip, TooltipTrigger } from "@artis/ui";
import { SideNavIconContainer } from "../../icons/SideNavIconContainer";
import { SidebarIconWrapper } from "../../layout/SidebarIconWrapper";
import { useStatusModalActions } from "./useStatusModalActions";
import { useReconfirmDataLogic } from "./useReconfirmDataLogic";
import { usePauseCalcs } from "../../calcs/react";
import { useSetAllColumnWidthsModal } from "./SetAllColumnWidthsModal";
import { useSetAllFuturesFieldsAction } from "./useSetAllFuturesFieldsAction";
import {
  usePageProducts,
  useOptionalActivePageId,
  useUpdatePageFormatting,
  useUpdatePageProducts,
  usePage,
} from "../../../data";
import { useConfirmation } from "../hooks/useConfirmation";
import {
  useQueryConditionalFormattingRulesGrouped,
  useDeleteConditionalFormattingRules,
} from "../../../data/formatting/formatting";
import { textBase, cardContainer } from "../../ui/shared-styles";
import { DebugIcon } from "../../icons/DebugIcon";
import { isNoneProduction } from "../../../globals";
import { useGrid, useSetSuppressHeaderTooltips } from "../../market-grid/stores";
import { colParams } from "../../utils";
import { columnWidth as defaultColumnWidth } from "../../globals";
import { useDevPanel } from "../../market-grid/components/DevPannel";
import { useUserSubscriptionTier } from "../../../data/user";
import { FlyoutPanel, isInsideActionsMenu } from "./submenuFlyout";

const FUTURES_SUBMENU_CLOSE_DELAY_MS = 500;

const NOT_APPLICABLE_TO_ALL_FUTURES = "Not applicable to all Futures Curves on this Page";

const menuItemClass = cn(
  "flex items-center gap-[length:var(--spacing-scale-small-200,6px)] p-[length:var(--spacing-scale-small-200,6px)]",
  "text-[14px] leading-[20px]",
  "text-[var(--foreground-default,#101010)] font-normal outline-none cursor-pointer",
  "hover:bg-neutral-tonal rounded-[var(--border-radius-rounded-sm,3px)]",
);

const disabledClass = "opacity-50 cursor-not-allowed pointer-events-none";

const subContentClass = cn("flex flex-col py-[var(--padding-small-150,4.5px)]", cardContainer);

function NotApplicableTooltip({ children }: { children: ReactNode }) {
  return (
    <TooltipTrigger block>
      {children}
      <Tooltip>{NOT_APPLICABLE_TO_ALL_FUTURES}</Tooltip>
    </TooltipTrigger>
  );
}

function NestedFuturesMenu({
  open,
  onMouseEnter,
  onMouseLeave,
  row,
  children,
}: {
  open: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  row: ReactNode;
  children: ReactNode;
}) {
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);

  return (
    <div ref={setAnchor} className="relative" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {row}
      {open && anchor && (
        <FlyoutPanel
          anchor={anchor}
          zIndex={202}
          className={cn(subContentClass, "min-w-[120px]")}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {children}
        </FlyoutPanel>
      )}
    </div>
  );
}

export function ActionsMenu() {
  const [open, setOpen] = useState(false);
  const [tooltipDisabled, setTooltipDisabled] = useState(false);
  const [openPopupCount, setOpenPopupCount] = useState(0);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [openSubSubmenu, setOpenSubSubmenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ bottom: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);
  const [futuresAnchor, setFuturesAnchor] = useState<HTMLDivElement | null>(null);
  const columnWidthsSubmenuRef = useRef<HTMLDivElement>(null);
  const submenuCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subSubmenuCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { disabledFeatures } = useUserSubscriptionTier();
  const debug = useDevPanel();
  const [gridApi] = useGrid();
  const updatePageProducts = useUpdatePageProducts();
  const setSuppressHeaderTooltips = useSetSuppressHeaderTooltips();

  useEffect(() => {
    setSuppressHeaderTooltips(open || openPopupCount > 0);
  }, [open, openPopupCount, setSuppressHeaderTooltips]);

  useEffect(() => {
    if (tooltipDisabled) {
      const timer = setTimeout(() => setTooltipDisabled(false), 500);
      return () => clearTimeout(timer);
    }
  }, [tooltipDisabled]);

  useEffect(() => {
    return () => {
      if (submenuCloseTimeoutRef.current) {
        clearTimeout(submenuCloseTimeoutRef.current);
      }
      if (subSubmenuCloseTimeoutRef.current) {
        clearTimeout(subSubmenuCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      if (openPopupCount > 0) return;
      if (isInsideActionsMenu(target, menuContentRef.current)) return;
      if (triggerRef.current?.contains(target)) return;

      setOpen(false);
      setOpenSubmenu(null);
      setOpenSubSubmenu(null);
    };

    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, openPopupCount]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({
      bottom: window.innerHeight - rect.bottom,
      left: rect.right + 8,
    });
  }, [open]);

  const getAnchorPosition = () => {
    if (columnWidthsSubmenuRef.current) {
      const rect = columnWidthsSubmenuRef.current.getBoundingClientRect();
      return { bottom: window.innerHeight - rect.bottom, left: rect.right + 8 };
    }

    if (menuContentRef.current) {
      const rect = menuContentRef.current.getBoundingClientRect();
      return { bottom: window.innerHeight - rect.bottom, left: rect.right + 8 };
    }

    if (!triggerRef.current) return { bottom: 16, left: 68 };
    const rect = triggerRef.current.getBoundingClientRect();
    return { bottom: window.innerHeight - rect.bottom, left: rect.right + 8 };
  };

  const closeMenu = () => {
    setTooltipDisabled(true);
    setOpen(false);
    setOpenSubmenu(null);
    setOpenSubSubmenu(null);
    setMenuPosition(null);
    if (submenuCloseTimeoutRef.current) {
      clearTimeout(submenuCloseTimeoutRef.current);
      submenuCloseTimeoutRef.current = null;
    }
    if (subSubmenuCloseTimeoutRef.current) {
      clearTimeout(subSubmenuCloseTimeoutRef.current);
      subSubmenuCloseTimeoutRef.current = null;
    }
  };

  const incrementPopupCount = () => setOpenPopupCount((prev) => prev + 1);
  const decrementPopupCount = () => setOpenPopupCount((prev) => Math.max(0, prev - 1));

  const closeMenuIfNoPopups = () => {
    if (openPopupCount <= 1) {
      closeMenu();
    }
  };

  const { openStatusModal, StatusModal } = useStatusModalActions(
    getAnchorPosition,
    incrementPopupCount,
    decrementPopupCount,
    closeMenuIfNoPopups,
    menuContentRef,
  );
  const [paused, setPaused] = usePauseCalcs();
  const pageId = useOptionalActivePageId();
  const pageProducts = usePageProducts(pageId).data;
  const hasGridCurves = pageProducts && pageProducts.length > 0;
  const page = usePage(pageId ?? "");
  const extraHeaderHeight = page.data?.extra_header_height ?? false;

  const { openModal: openColumnWidthsModal, ColumnWidthsModal } = useSetAllColumnWidthsModal(
    getAnchorPosition,
    incrementPopupCount,
    decrementPopupCount,
    closeMenuIfNoPopups,
    menuContentRef,
  );
  const { futuresFieldMenuItems, currentFieldSelector, hasFuturesProducts } = useSetAllFuturesFieldsAction();

  const confirm = useConfirmation();

  const { hasDataToReconfirm, handleConfirm: handleReconfirmDataConfirm } = useReconfirmDataLogic();

  const updateFormatting = useUpdatePageFormatting();
  const rules = useQueryConditionalFormattingRulesGrouped(pageId ?? "");
  const deleteRules = useDeleteConditionalFormattingRules(pageId ?? "");

  const handleTriggerClick = () => {
    if (open) {
      if (openPopupCount === 0) {
        closeMenu();
      }
    } else {
      setMenuPosition(null);
      setOpen(true);
      setTooltipDisabled(true);
    }
  };

  const handleChangeAllSources = () => {
    if (disabledFeatures?.changeSource) return;
    openStatusModal();
  };

  const handleReconfirmData = () => {
    if (!hasDataToReconfirm) return;
    closeMenu();
    confirm({
      title: "Reconfirm Data",
      message: "This will affect all manual entry data cells on this Page.",
      confirmButtonText: "Reconfirm Data",
      onConfirm: handleReconfirmDataConfirm,
    });
  };

  const handlePauseExchangeData = () => {
    if (disabledFeatures?.pauseExchangeData || !hasGridCurves) return;
    closeMenu();
    setPaused(!paused);
  };

  const handleSetAllColumnWidths = () => {
    openColumnWidthsModal();
  };

  const handleSetAllColumnWidthsToFit = async () => {
    closeMenu();

    if (!gridApi || gridApi.isDestroyed() || !pageProducts) return;

    const columns =
      gridApi.getColumns()?.filter((column) => {
        const col = colParams(column);
        return col?.gridId && col?.artisType;
      }) || [];

    if (columns.length === 0) return;

    const calculateOptimalWidth = (column: Column) => {
      const colDef = column.getColDef();
      const headerParams = colDef?.headerComponentParams;
      const displayName = colDef?.headerName || "";
      const curveName = headerParams?.columnLabels?.description || "";

      const productName = curveName.length > displayName.length ? curveName : displayName;

      const charWidth = 7.5;
      const padding = 16;
      const maxLines = 2;
      const minWidth = defaultColumnWidth;
      const maxWidth = 200;

      const textLength = productName.length;
      const charsPerLineAtMin = Math.floor((minWidth - padding) / charWidth);
      const totalCharsIn2Lines = charsPerLineAtMin * maxLines;

      if (textLength <= totalCharsIn2Lines) {
        return minWidth;
      }

      const charsPerLineNeeded = Math.ceil(textLength / maxLines);
      const widthNeeded = charsPerLineNeeded * charWidth + padding;
      // charWidth is fractional, and column_width only accepts whole pixels.
      return Math.round(Math.min(Math.max(widthNeeded, minWidth), maxWidth));
    };

    const changes = columns
      .map((column) => {
        const col = colParams(column);
        const gridId = col?.gridId;
        if (!gridId) return null;
        const current = pageProducts?.find((x) => (x.grid_id || x.id) === gridId);
        if (!current) return null;
        const newWidth = calculateOptimalWidth(column);
        return updatePageProducts.optimistic({
          id: current.id,
          column_width: newWidth,
        });
      })
      .filter(Boolean);

    await Promise.all(changes);
  };

  const handleClearPageFormatting = () => {
    closeMenu();
    confirm({
      title: "Clear all formatting from Page?",
      message: "You're about to remove all formatting from the current Page.\n\nThis action can't be undone.",
      variant: "danger",
      confirmButtonText: "Clear All Formatting",
      onConfirm: () => {
        if (!pageId) return;

        // Clear all highlights
        updateFormatting({
          id: pageId,
          cell_highlights: {},
          column_highlights: {},
          period_highlights: {},
        });

        // Clear all conditional formatting rules
        const allRuleIds =
          Object.values(rules.data || {})
            .flatMap((ruleArray) => ruleArray?.map((rule) => rule.id))
            .filter(Boolean) || [];

        if (allRuleIds.length > 0) {
          deleteRules.mutate(allRuleIds);
        }
      },
    });
  };

  const isChangeSourceDisabled = disabledFeatures?.changeSource;
  const isSetAllFuturesFieldsDisabled = disabledFeatures?.setAllFuturesFields || !hasFuturesProducts;
  const isReconfirmDisabled = !hasDataToReconfirm;
  const isPauseDisabled = disabledFeatures?.pauseExchangeData || !hasGridCurves;

  const keepFuturesSubmenuOpen = () => {
    if (submenuCloseTimeoutRef.current) {
      clearTimeout(submenuCloseTimeoutRef.current);
      submenuCloseTimeoutRef.current = null;
    }
    if (!isSetAllFuturesFieldsDisabled) {
      setOpenSubmenu("futures");
    }
  };

  const scheduleFuturesSubmenuClose = () => {
    if (submenuCloseTimeoutRef.current) {
      clearTimeout(submenuCloseTimeoutRef.current);
    }
    submenuCloseTimeoutRef.current = setTimeout(() => {
      submenuCloseTimeoutRef.current = null;
      setOpenSubmenu(null);
      setOpenSubSubmenu(null);
    }, FUTURES_SUBMENU_CLOSE_DELAY_MS);
  };

  const keepNestedSubmenuOpen = (value: string) => {
    if (subSubmenuCloseTimeoutRef.current) {
      clearTimeout(subSubmenuCloseTimeoutRef.current);
      subSubmenuCloseTimeoutRef.current = null;
    }
    setOpenSubSubmenu(value);
  };

  const scheduleNestedSubmenuClose = () => {
    if (subSubmenuCloseTimeoutRef.current) {
      clearTimeout(subSubmenuCloseTimeoutRef.current);
    }
    subSubmenuCloseTimeoutRef.current = setTimeout(() => {
      subSubmenuCloseTimeoutRef.current = null;
      setOpenSubSubmenu(null);
    }, FUTURES_SUBMENU_CLOSE_DELAY_MS);
  };

  return (
    <>
      <TooltipTrigger placement="right" disabled={open || tooltipDisabled}>
        <SidebarIconWrapper
          ref={triggerRef}
          className="cursor-pointer"
          onClick={handleTriggerClick}
          data-testid="actions-button"
        >
          <SideNavIconContainer>
            <ActionsIcon selected={open} />
          </SideNavIconContainer>
        </SidebarIconWrapper>
        <Tooltip>Actions</Tooltip>
      </TooltipTrigger>

      {open &&
        menuPosition &&
        createPortal(
          <div
            ref={menuContentRef}
            data-testid="actions-menu"
            data-menu-surface
            className={cn(
              "fixed z-[200] min-w-[180px] inline-flex flex-col justify-center items-center",
              "p-[var(--padding-small-200,6px)]",
              cardContainer,
            )}
            style={{
              bottom: menuPosition.bottom,
              left: menuPosition.left,
            }}
          >
            <div
              className={cn(
                "flex px-[var(--padding-small-200,6px)] py-[var(--padding-small-100,3px)] justify-start items-center gap-[10px] self-stretch text-[var(--foreground-default,#101010)] font-medium",
                textBase,
              )}
            >
              Actions
            </div>

            <div className="flex flex-col mt-[var(--padding-small-100,3px)] gap-[var(--spacing-scale-small-200,6px)]">
              <div
                data-testid="actions-change-all-sources"
                className={cn(menuItemClass, isChangeSourceDisabled && disabledClass)}
                onClick={isChangeSourceDisabled ? undefined : handleChangeAllSources}
              >
                <MenuItemIconWrapper>
                  <ChangeSourceIcon />
                </MenuItemIconWrapper>
                <span>Change all sources</span>
              </div>

              {/* Set all Futures fields submenu */}
              <TooltipTrigger disabled={!isSetAllFuturesFieldsDisabled || disabledFeatures?.setAllFuturesFields}>
                <div
                  ref={setFuturesAnchor}
                  className="relative"
                  onMouseEnter={keepFuturesSubmenuOpen}
                  onMouseLeave={scheduleFuturesSubmenuClose}
                >
                  <div
                    data-testid="actions-futures-fields"
                    className={cn(menuItemClass, "justify-between", isSetAllFuturesFieldsDisabled && disabledClass)}
                  >
                    <div className="flex items-center gap-[var(--spacing-scale-small-200,6px)]">
                      <MenuItemIconWrapper>
                        <FuturesFieldIcon />
                      </MenuItemIconWrapper>
                      <span>Set all Futures fields</span>
                    </div>
                    <MenuItemIconWrapper>
                      <ChevronRightIcon />
                    </MenuItemIconWrapper>
                  </div>

                  {openSubmenu === "futures" && futuresAnchor && !isSetAllFuturesFieldsDisabled && (
                    <FlyoutPanel
                      anchor={futuresAnchor}
                      testId="futures-fields-submenu"
                      zIndex={201}
                      className={cn(subContentClass, "min-w-[150px]")}
                      onMouseEnter={keepFuturesSubmenuOpen}
                      onMouseLeave={scheduleFuturesSubmenuClose}
                    >
                      {futuresFieldMenuItems.map((item, index) => {
                        const isChgfv = item.value === "chgfv";
                        const nextItem = futuresFieldMenuItems[index + 1];
                        const hasSubmenuAfter = nextItem?.subItems;

                        if (item.subItems) {
                          const row = (
                            <div
                              className={cn(menuItemClass, "justify-between", item.disabled && disabledClass)}
                              style={item.disabled ? { pointerEvents: "auto" } : undefined}
                            >
                              <span>{item.label}</span>
                              <MenuItemIconWrapper>
                                <ChevronRightIcon />
                              </MenuItemIconWrapper>
                            </div>
                          );

                          return (
                            <NestedFuturesMenu
                              key={item.value}
                              open={openSubSubmenu === item.value && !item.disabled}
                              onMouseEnter={() => {
                                if (item.disabled) return;
                                keepNestedSubmenuOpen(item.value);
                              }}
                              onMouseLeave={scheduleNestedSubmenuClose}
                              row={item.disabled ? <NotApplicableTooltip>{row}</NotApplicableTooltip> : row}
                            >
                              {item.subItems.map((subItem) => {
                                const subRow = (
                                  <div
                                    className={cn(menuItemClass, "justify-between", subItem.disabled && disabledClass)}
                                    style={subItem.disabled ? { pointerEvents: "auto" } : undefined}
                                    onClick={
                                      subItem.disabled
                                        ? undefined
                                        : () => {
                                            closeMenu();
                                            subItem.action();
                                          }
                                    }
                                  >
                                    <span>{subItem.label}</span>
                                    {currentFieldSelector === subItem.value && (
                                      <LuCheck className="w-4 h-4 text-primary" />
                                    )}
                                  </div>
                                );

                                return (
                                  <div key={subItem.value}>
                                    {subItem.disabled ? <NotApplicableTooltip>{subRow}</NotApplicableTooltip> : subRow}
                                  </div>
                                );
                              })}
                            </NestedFuturesMenu>
                          );
                        }

                        const row = (
                          <div
                            data-testid="futures-field-item"
                            data-value={item.value}
                            data-selected={String(currentFieldSelector === item.value)}
                            className={cn(menuItemClass, "justify-between", item.disabled && disabledClass)}
                            style={item.disabled ? { pointerEvents: "auto" } : undefined}
                            onClick={
                              item.disabled
                                ? undefined
                                : () => {
                                    closeMenu();
                                    item.action();
                                  }
                            }
                          >
                            <span>{item.label}</span>
                            {currentFieldSelector === item.value && <LuCheck className="w-4 h-4 text-primary" />}
                          </div>
                        );

                        return (
                          <div key={item.value}>
                            {item.disabled ? <NotApplicableTooltip>{row}</NotApplicableTooltip> : row}
                            {isChgfv && hasSubmenuAfter && (
                              <div className="h-px bg-[var(--outline-default,#BBB)] my-1 w-full" />
                            )}
                          </div>
                        );
                      })}
                    </FlyoutPanel>
                  )}
                </div>
                <Tooltip>This Page has no Futures</Tooltip>
              </TooltipTrigger>

              {!disabledFeatures?.reconfirmData && (
                <div
                  data-testid="actions-reconfirm-data"
                  className={cn(menuItemClass, isReconfirmDisabled && disabledClass)}
                  onClick={isReconfirmDisabled ? undefined : handleReconfirmData}
                >
                  <MenuItemIconWrapper>
                    <ReconfirmDataIcon />
                  </MenuItemIconWrapper>
                  <span>Reconfirm all data</span>
                </div>
              )}

              <div
                data-testid="actions-pause-exchange-data"
                className={cn(menuItemClass, isPauseDisabled && disabledClass)}
                onClick={isPauseDisabled ? undefined : handlePauseExchangeData}
              >
                <MenuItemIconWrapper>
                  <PauseExchangeIcon />
                </MenuItemIconWrapper>
                <span>{paused ? "Resume exchange data" : "Pause exchange data"}</span>
              </div>

              <div
                className="relative"
                onMouseEnter={() => {
                  if (submenuCloseTimeoutRef.current) {
                    clearTimeout(submenuCloseTimeoutRef.current);
                    submenuCloseTimeoutRef.current = null;
                  }
                  setOpenSubmenu("columnWidths");
                }}
                onMouseLeave={() => {
                  if (openPopupCount === 0) {
                    submenuCloseTimeoutRef.current = setTimeout(() => {
                      setOpenSubmenu(null);
                    }, 300);
                  }
                }}
              >
                <div data-testid="actions-set-column-widths" className={cn(menuItemClass, "justify-between")}>
                  <div className="flex items-center gap-[var(--spacing-scale-small-200,6px)]">
                    <MenuItemIconWrapper>
                      <ColumnWidthsIcon />
                    </MenuItemIconWrapper>
                    <span>Set all column widths</span>
                  </div>
                  <MenuItemIconWrapper>
                    <ChevronRightIcon />
                  </MenuItemIconWrapper>
                </div>

                {(openSubmenu === "columnWidths" || (openPopupCount > 0 && ColumnWidthsModal)) && (
                  <>
                    <div className="absolute left-full top-0 w-3 h-full" />
                    <div
                      ref={columnWidthsSubmenuRef}
                      className={cn(subContentClass, "absolute left-full top-0 ml-3 z-[201] min-w-[150px]")}
                      onMouseEnter={() => {
                        if (submenuCloseTimeoutRef.current) {
                          clearTimeout(submenuCloseTimeoutRef.current);
                          submenuCloseTimeoutRef.current = null;
                        }
                        setOpenSubmenu("columnWidths");
                      }}
                      onMouseLeave={() => {
                        if (openPopupCount === 0) {
                          submenuCloseTimeoutRef.current = setTimeout(() => {
                            setOpenSubmenu(null);
                          }, 300);
                        }
                      }}
                    >
                      <div
                        data-testid="actions-column-widths-custom"
                        className={menuItemClass}
                        onClick={handleSetAllColumnWidths}
                      >
                        <span>To custom value</span>
                      </div>
                      {extraHeaderHeight ? (
                        <div className={menuItemClass} onClick={handleSetAllColumnWidthsToFit}>
                          <span>To fit content</span>
                        </div>
                      ) : (
                        <TooltipTrigger>
                          <div className="w-full">
                            <div
                              className={cn(menuItemClass, disabledClass)}
                              onClick={(e) => e.preventDefault()}
                              style={{ pointerEvents: "auto", cursor: "not-allowed" }}
                            >
                              <span>To fit content</span>
                            </div>
                          </div>
                          <Tooltip>Enable Full Header Height to use this feature</Tooltip>
                        </TooltipTrigger>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div data-testid="actions-clear-formatting" className={menuItemClass} onClick={handleClearPageFormatting}>
                <MenuItemIconWrapper>
                  <ClearFormattingIcon />
                </MenuItemIconWrapper>
                <span>Clear Page Formatting</span>
              </div>

              {isNoneProduction && (
                <div
                  className={menuItemClass}
                  onClick={() => {
                    closeMenu();
                    debug.open();
                  }}
                >
                  <MenuItemIconWrapper>
                    <DebugIcon />
                  </MenuItemIconWrapper>
                  <span>Debug panel</span>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}

      {StatusModal}
      {ColumnWidthsModal}
    </>
  );
}
